import React, { useMemo, useEffect, useRef, useState } from "react";
import { createPortal } from "@react-three/fiber";
import { Decal } from "@react-three/drei";
import { useSnapshot } from "valtio";
import * as THREE from "three";
import { logoTextState } from "../config/logoTextState";
import { createTextTexture } from "../utils/createTextTexture";
import { modelConfig } from "../config/models";

const FACE_DIR = {
  front:  new THREE.Vector3( 0,  0, -1),
  back:   new THREE.Vector3( 0,  0,  1),
  left:   new THREE.Vector3( 1,  0,  0),
  right:  new THREE.Vector3(-1,  0,  0),
  top:    new THREE.Vector3( 0, -1,  0),
  bottom: new THREE.Vector3( 0,  1,  0),
};

// Cast a ray and return the closest hit as {hitMesh, worldNormal, worldHit}, or null.
function raycastSurface(origin, dir, meshes) {
  const raycaster = new THREE.Raycaster();
  raycaster.set(origin, dir);
  const hits = raycaster.intersectObjects(meshes, false);
  if (hits.length === 0) return null;
  const hit = hits[0];
  const worldNormal = hit.face.normal.clone()
    .transformDirection(hit.object.matrixWorld).normalize();
  const worldHit = hit.point.clone().addScaledVector(worldNormal, 0.002);
  return { hitMesh: hit.object, worldNormal, worldHit };
}

// Build the local (mesh-space) position/orientation/scale a Decal needs from a world-space hit.
function buildLocalFrame(hitMesh, worldHit, worldNormal) {
  const matInv   = hitMesh.matrixWorld.clone().invert();
  const localPos = hitMesh.worldToLocal(worldHit.clone());

  const localNormal = worldNormal.clone().transformDirection(matInv).normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 0, 1),
    localNormal
  );
  const localRotation = new THREE.Euler().setFromQuaternion(quat);

  const meshWorldScale = new THREE.Vector3();
  hitMesh.getWorldScale(meshWorldScale);
  const avgScale = (meshWorldScale.x + meshWorldScale.y + meshWorldScale.z) / 3;

  return { hitMesh, localPos, localRotation, avgScale };
}

/**
 * Multi-overlay logo/text renderer.
 * Renders all placed items from logoTextState.items + a live preview of the
 * current editor content. Each item is portaled as a <Decal> into the
 * surface mesh found via raycast.
 */
export default function LogoTextOverlay({ modelGroupRef, modelName }) {
  const snap = useSnapshot(logoTextState);

  // Per-placement cache built after model loads
  const setupCache = useRef({});
  const meshesRef = useRef([]);
  const [cacheReady, setCacheReady] = useState(0);

  // ── One-time setup ───────────────────────────────────────────────────────
  useEffect(() => {
    setupCache.current = {};
    meshesRef.current = [];
    setCacheReady(0);

    const t = setTimeout(() => {
      if (!modelGroupRef?.current) return;

      const meshes = [];
      modelGroupRef.current.traverse((child) => {
        if (child.isMesh && !(child.material instanceof THREE.ShadowMaterial) && !child.userData.isDesignOverlay) {
          meshes.push(child);
        }
      });
      if (!meshes.length) return;
      meshesRef.current = meshes;

      const worldBox = new THREE.Box3().setFromObject(modelGroupRef.current);
      const wCenter  = new THREE.Vector3();
      const wSize    = new THREE.Vector3();
      worldBox.getCenter(wCenter);
      worldBox.getSize(wSize);

      const cfg = modelConfig[modelName];
      const placementsCfg = cfg?.placements || {
        front: { dir: "front", rayHeight: 0.35 },
        back:  { dir: "back",  rayHeight: 0.35 },
      };

      Object.keys(placementsCfg).forEach((placementKey) => {
        const pc = placementsCfg[placementKey];
        const dir = FACE_DIR[pc.dir];
        if (!dir) return;

        const origin = wCenter.clone();
        const margin = Math.max(wSize.x, wSize.y, wSize.z) * 0.5 + 0.3;
        const rayY = worldBox.min.y + wSize.y * (pc.rayHeight ?? 0.5);

        switch (pc.dir) {
          case "front":  origin.z = worldBox.max.z + margin; origin.y = rayY; break;
          case "back":   origin.z = worldBox.min.z - margin; origin.y = rayY; break;
          case "left":   origin.x = worldBox.min.x - margin; origin.y = rayY; break;
          case "right":  origin.x = worldBox.max.x + margin; origin.y = rayY; break;
          case "top":    origin.y = worldBox.max.y + margin; break;
          case "bottom": origin.y = worldBox.min.y - margin; break;
          default: break;
        }

        const hit = raycastSurface(origin, dir, meshes);

        let hitMesh, worldNormal, worldHit;

        if (hit) {
          ({ hitMesh, worldNormal, worldHit } = hit);
        } else {
          hitMesh = meshes.reduce((a, b) =>
            (a.geometry?.attributes?.position?.count || 0) >=
            (b.geometry?.attributes?.position?.count || 0) ? a : b
          );
          worldNormal = dir.clone().negate();
          worldHit    = wCenter.clone();
          switch (pc.dir) {
            case "front":  worldHit.z = worldBox.max.z + 0.002; worldHit.y = rayY; break;
            case "back":   worldHit.z = worldBox.min.z - 0.002; worldHit.y = rayY; break;
            case "left":   worldHit.x = worldBox.min.x - 0.002; worldHit.y = rayY; break;
            case "right":  worldHit.x = worldBox.max.x + 0.002; worldHit.y = rayY; break;
            case "top":    worldHit.y = worldBox.max.y + 0.002; break;
            case "bottom": worldHit.y = worldBox.min.y - 0.002; break;
            default: break;
          }
        }

        const worldUp   = new THREE.Vector3(0, 1, 0);
        let wTangent    = new THREE.Vector3().crossVectors(worldUp, worldNormal);
        if (wTangent.lengthSq() < 0.001)
          wTangent.crossVectors(new THREE.Vector3(0, 0, 1), worldNormal);
        wTangent.normalize();
        const wBitangent = new THREE.Vector3()
          .crossVectors(worldNormal, wTangent).normalize();

        // World-space step size per offset unit (matches the old local-space
        // stepH/stepV magnitude — the avgScale factor that used to divide
        // here canceled out when converting local displacement back to world).
        const stepH = (pc.dir === "left" || pc.dir === "right") ? wSize.z * 0.1 : wSize.x * 0.1;
        const stepV = (pc.dir === "top" || pc.dir === "bottom") ? wSize.z * 0.1 : wSize.y * 0.1;

        setupCache.current[placementKey] = {
          // Far-away base ray origin + direction, so DecalItem can re-raycast
          // from a shifted origin as the user drags — this follows the
          // model's actual curvature instead of sliding across one flat
          // plane computed at (offsetX, offsetY) = (0, 0).
          origin: origin.clone(),
          dir: dir.clone(),
          wTangent, wBitangent,
          stepH, stepV,
          // Fallback frame (flat slide) for when a dragged position ends up
          // off the edge of the model and the re-raycast finds nothing.
          fallbackHitMesh: hitMesh,
          fallbackWorldNormal: worldNormal,
          fallbackWorldHit: worldHit,
        };
      });

      setCacheReady((n) => n + 1);
    }, 400);

    return () => clearTimeout(t);
  }, [modelGroupRef, modelName]);

  if (!cacheReady) return null;

  // Extract plain values from snapshot (valtio proxy spread may not work for useMemo deps)
  const editorValues = {
    activeTab: snap.activeTab,
    logo: snap.logo,
    text: snap.text,
    font: snap.font,
    textColor: snap.textColor,
    bold: snap.bold,
    curved: snap.curved,
    curveUp: snap.curveUp,
    placement: snap.placement,
    offsetX: snap.offsetX,
    offsetY: snap.offsetY,
    size: snap.size,
    rotation: snap.rotation,
  };

  // Build list of overlays to render:
  // - All placed items (using saved values, or editor values if being edited)
  // - Live preview if creating new item with content
  const overlays = [];

  snap.items.forEach((item) => {
    if (item.id === snap.editingId) {
      // Render with current editor values for live editing
      overlays.push({ ...editorValues, _key: `item-${item.id}` });
    } else {
      overlays.push({
        activeTab: item.activeTab, logo: item.logo, text: item.text,
        font: item.font, textColor: item.textColor, bold: item.bold,
        curved: item.curved, curveUp: item.curveUp, placement: item.placement,
        offsetX: item.offsetX, offsetY: item.offsetY, size: item.size,
        rotation: item.rotation, _key: `item-${item.id}`,
      });
    }
  });

  // Live preview for new item (only when not editing existing)
  if (snap.editingId === null) {
    const hasPreviewContent =
      (editorValues.activeTab === "logo" && editorValues.logo) ||
      (editorValues.activeTab === "text" && editorValues.text.trim());
    if (hasPreviewContent) {
      overlays.push({ ...editorValues, _key: "preview" });
    }
  }

  return (
    <>
      {overlays.map((overlay) => (
        <DecalItem
          key={overlay._key}
          overlay={overlay}
          cache={setupCache.current}
          meshesRef={meshesRef}
        />
      ))}
    </>
  );
}

/**
 * Renders a single Decal for one overlay item.
 */
function DecalItem({ overlay, cache, meshesRef }) {
  const entry = cache[overlay.placement];

  // ── Text texture ──────────────────────────────────────────────────────
  const textCanvas = useMemo(() => {
    if (overlay.activeTab !== "text" || !overlay.text?.trim()) return null;
    return createTextTexture({
      text: overlay.text, font: overlay.font, textColor: overlay.textColor,
      bold: overlay.bold, curved: overlay.curved, curveUp: overlay.curveUp,
    });
  }, [overlay.activeTab, overlay.text, overlay.font, overlay.textColor,
      overlay.bold, overlay.curved, overlay.curveUp]);

  const textTexture = useMemo(() => {
    if (!textCanvas) return null;
    const tex = new THREE.CanvasTexture(textCanvas);
    tex.needsUpdate = true;
    return tex;
  }, [textCanvas]);

  // ── Logo texture ──────────────────────────────────────────────────────
  const [logoTexture, setLogoTexture] = useState(null);
  const [logoAspect, setLogoAspect] = useState(1);
  const logoSrc = overlay.activeTab === "logo" ? overlay.logo : null;
  useEffect(() => {
    if (!logoSrc) { setLogoTexture(null); return; }
    new THREE.TextureLoader().load(logoSrc, (tex) => {
      tex.needsUpdate = true;
      setLogoAspect(tex.image.width / tex.image.height);
      setLogoTexture(tex);
    });
  }, [logoSrc]);

  const activeTexture = overlay.activeTab === "logo" ? logoTexture : textTexture;
  // Text canvases are always square (1024x1024); only logo images can be non-square.
  const activeAspect = overlay.activeTab === "logo" ? logoAspect : 1;

  // ── Surface frame (position/rotation/scale basis) ───────────────────────
  // Re-raycasts from a shifted origin on every offset change, so dragging a
  // decal across curved geometry follows the actual surface instead of
  // sliding across one flat plane computed at the original hit point.
  const surfaceFrame = useMemo(() => {
    if (!entry) return null;
    const shiftedOrigin = entry.origin.clone()
      .addScaledVector(entry.wTangent,   overlay.offsetX * entry.stepH)
      .addScaledVector(entry.wBitangent, overlay.offsetY * entry.stepV);

    const hit = raycastSurface(shiftedOrigin, entry.dir, meshesRef.current);
    const { hitMesh, worldNormal, worldHit } = hit || {
      hitMesh: entry.fallbackHitMesh,
      worldNormal: entry.fallbackWorldNormal,
      worldHit: entry.fallbackWorldHit,
    };
    return buildLocalFrame(hitMesh, worldHit, worldNormal);
  }, [entry, meshesRef, overlay.offsetX, overlay.offsetY]);

  const localScale = useMemo(() => {
    if (!surfaceFrame) return 0.1;
    return overlay.size / surfaceFrame.avgScale;
  }, [surfaceFrame, overlay.size]);

  // Fit the (possibly non-square) source image inside the localScale box
  // without distorting it — scale the wider axis down to match its aspect.
  const localScaleX = activeAspect >= 1 ? localScale : localScale * activeAspect;
  const localScaleY = activeAspect >= 1 ? localScale / activeAspect : localScale;

  const localRotation = useMemo(() => {
    if (!surfaceFrame) return new THREE.Euler();
    const baseQuat = new THREE.Quaternion().setFromEuler(surfaceFrame.localRotation);
    const localNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(baseQuat).normalize();
    const userQuat = new THREE.Quaternion().setFromAxisAngle(
      localNormal,
      (overlay.rotation * Math.PI) / 180
    );
    const combined = userQuat.multiply(baseQuat);
    return new THREE.Euler().setFromQuaternion(combined);
  }, [surfaceFrame, overlay.rotation]);

  if (!activeTexture || !surfaceFrame) return null;

  return createPortal(
    <Decal
      ref={(mesh) => {
        if (mesh) {
          mesh.renderOrder = 10;
        }
      }}
      position={surfaceFrame.localPos.toArray()}
      rotation={localRotation}
      scale={[localScaleX, localScaleY, localScale * 1.3]}
    >
      <meshStandardMaterial
        map={activeTexture}
        transparent
        alphaTest={0.5}
        depthWrite={false}
        depthTest={true}
        roughness={0.85}
        metalness={0}
        polygonOffset
        polygonOffsetFactor={-10}
        polygonOffsetUnits={-10}
      />
    </Decal>,
    surfaceFrame.hitMesh
  );
}
